import { IoMdInformationCircleOutline } from "react-icons/io";
import { updateSelectedIssues } from "redux/Modals/actions";
import { useNavigate } from "react-router";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { useDispatch } from "react-redux";
import { UniquenessData } from "types";
import classNames from "classnames";
import { useState } from "react";
import {
  DUPLICATE_THRESHOLD,
  ENTITY_TYPE_PARAMETER,
  ISSUE_TYPE_PARAMETER,
} from "helpers/constants/miscellaneous";

import styles from "./style.module.scss";

interface SingleRowProps {
  index: number;
  singleIssue: any;
  selectedIssues: any[];
  uniquenessData: UniquenessData[];
  datasetId: string;
}

const SingleRow = ({
  index,
  singleIssue,
  selectedIssues,
  uniquenessData,
  datasetId,
}: SingleRowProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const {
    id,
    cause,
    color,
    objectName,
    confidence,
    boundingBox,
    description,
    type_display_name,
    duplicate_threshold = null,
  } = singleIssue;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMislabeled = type_display_name === "mislabels";
  const isDuplicate = type_display_name === "duplicates";
  const isDuplicateWithThreshhold = isDuplicate && duplicate_threshold;

  const showLink = isMislabeled && boundingBox;
  const selectedIssue = selectedIssues.find(
    (issue: any) => issue.cause === cause,
  );
  const isSelected = selectedIssue && selectedIssue.id === id;

  const subjectStyles = {
    color: (objectName && isHovering) || isSelected ? color : undefined,
  };
  const dynamicDescription = isDuplicateWithThreshhold
    ? "Similarity Level " + duplicate_threshold
    : description;

  const subjectClassnames = classNames(styles.singleLabel, styles.subject, {
    [styles.lightFont]: showLink && !isSelected,
    [styles.boldFont]: showLink && isSelected,
    [styles.flexBox]: boundingBox && cause,
    [styles.detailWithLink]: (isHovering && showLink) || isSelected,
  });

  const singleSolutionClassnames = classNames(
    styles.singleLabel,
    styles.singleSolution,
    {
      [styles.thresholdLink]: isDuplicateWithThreshhold,
    },
  );

  const handleIssueClick = (issueObject: string) => {
    if (showLink) {
      dispatch(updateSelectedIssues(issueObject) as any);
    }
  };

  const handleDetailsClick = () => {
    if (!isDuplicateWithThreshhold) {
      return;
    }

    let path = `/dataset/${datasetId}/data`;

    const query = new URLSearchParams();
    query.set(DUPLICATE_THRESHOLD, JSON.stringify(duplicate_threshold));
    query.set(ISSUE_TYPE_PARAMETER, JSON.stringify([2]));
    if (cause) {
      query.set(ENTITY_TYPE_PARAMETER, "OBJECTS");
    }

    navigate(`${path}?${query.toString()}`);
  };

  const renderSubjectName = () => {
    if (!cause) {
      return "Entire Image";
    }
    return (
      <div
        title={objectName}
        className={styles.entityName}
        onMouseOver={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {objectName}
      </div>
    );
  };

  const renderSubLabelInfo = () => {
    if (!cause) {
      return <noscript />;
    }

    const objectUniquenessScore =
      uniquenessData.find((item) =>
        item.objectId ? item.objectId === cause : false,
      )?.uniquenessScore || null;

    let uniquenessScoreLine = <noscript />;
    let objectSizeLine = <noscript />;

    if (boundingBox) {
      const [, , width, height] = boundingBox;
      const objectSizeText = `${height}x${width}`;

      objectSizeLine = <span>{`object_size: ${objectSizeText}`}</span>;
    }

    if (typeof objectUniquenessScore === "number") {
      uniquenessScoreLine = (
        <span>{`uniqueness_score: ${objectUniquenessScore}`}</span>
      );
    }

    const content = (
      <div className={styles.objectToolTipTextContainer}>
        <span>{`object_id: ${cause}`}</span>
        {objectSizeLine}
        {uniquenessScoreLine}
      </div>
    );

    return (
      <div
        className={styles.infoIcon}
        onClick={(e) => e.stopPropagation()}
        data-tooltip-id={"image-details-issue-info-tooltip" + index}
      >
        <IoMdInformationCircleOutline size="16px" />
        <TooltipWrapper
          maxWidth="max-content"
          id={"image-details-issue-info-tooltip" + index}
          content={content}
        />
      </div>
    );
  };

  return (
    <tr className={styles.singleLabelRow}>
      <td className={`${styles.singleLabel} ${styles.issue}`}>
        {type_display_name}
      </td>
      <td
        style={subjectStyles}
        className={subjectClassnames}
        onClick={() => handleIssueClick(singleIssue)}
      >
        {renderSubjectName()}
        {renderSubLabelInfo()}
      </td>
      <td className={`${styles.singleLabel} ${styles.confidence}`}>
        {Math.round(confidence * 100)}%
      </td>
      <td className={styles.details} onClick={handleDetailsClick}>
        <span className={singleSolutionClassnames}>{dynamicDescription}</span>
      </td>
    </tr>
  );
};

const IssuesBox = ({
  issues,
  selectedIssues,
  uniquenessData,
  datasetId,
}: {
  datasetId: string;
  issues: any[];
  selectedIssues: any[];
  uniquenessData: UniquenessData[];
}) => {
  return issues && issues.length > 0 ? (
    <table className={styles.issuesTable}>
      <thead className={styles.darkBackground}>
        <tr className={styles.singleLabelRow}>
          <th className={`${styles.singleLabel} ${styles.issue}`}>Issue</th>
          <th className={`${styles.singleLabel} ${styles.subject}`}>Entity</th>
          <th className={`${styles.singleLabel} ${styles.confidence}`}>
            Confidence
          </th>
          <th className={`${styles.singleLabel} ${styles.details}`}>Details</th>
        </tr>
      </thead>
      <tbody>
        {issues.map((singleIssue, index) => (
          <SingleRow
            key={index}
            index={index}
            singleIssue={singleIssue}
            selectedIssues={selectedIssues}
            uniquenessData={uniquenessData}
            datasetId={datasetId}
          />
        ))}
      </tbody>
    </table>
  ) : null;
};

export default IssuesBox;
